import { useEffect } from "react";
import { Link } from "react-router-dom";
import { setPageMeta } from "@/lib/api";
import { Splatter } from "@/components/Splatter";
import { Layers, ShieldCheck, Waves, AlertTriangle, XCircle } from "lucide-react";

const MATERIALS = [
  {
    icon: Layers, color: "#E6007E", name: "PLA",
    tag: "Polyvalent & précis",
    desc: "Le matériau le plus courant en impression 3D. Facile à imprimer, il offre un très beau rendu et de fins détails. Idéal pour la décoration, les figurines, les objets d'intérieur et les cadeaux. Sensible à la chaleur : à éviter en plein soleil ou dans une voiture l'été.",
  },
  {
    icon: ShieldCheck, color: "#0055FF", name: "PETG",
    tag: "Résistant & durable",
    desc: "Plus solide et plus résistant à la chaleur et à l'humidité que le PLA. Parfait pour les objets fonctionnels, les pièces qui subissent des contraintes, les supports ou les objets destinés à l'extérieur. C'est le matériau que nous conseillons pour les lithophanies proches d'une source de chaleur.",
  },
  {
    icon: Waves, color: "#E6007E", name: "TPU",
    tag: "Souple & flexible",
    desc: "Un matériau flexible, semblable à du caoutchouc. Utilisé pour les pièces qui doivent plier, amortir ou adhérer : protections, joints, coques souples, bracelets, patins antidérapants. Impression plus technique, réservée aux besoins spécifiques.",
  },
];

export default function Materials() {
  useEffect(() => { setPageMeta("Nos matériaux — Tenay Ain-Pression", "PLA, PETG, TPU : nos matériaux d'impression 3D. ABS uniquement sur commande spéciale après étude, pas de résine."); }, []);
  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <Splatter />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <h1 className="font-heading text-4xl sm:text-5xl font-black">Nos <span className="text-[#E6007E]">matériaux</span></h1>
          <p className="mt-3 text-gray-400 max-w-2xl">Chaque projet a son matériau idéal. Voici ceux que nous utilisons et leurs usages recommandés.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-6 lg:grid-cols-3">
        {MATERIALS.map((m, i) => (
          <div key={m.name} className="fade-up card-hover rounded-2xl border border-[#222] bg-[#0A0A0A] p-7" style={{ animationDelay: `${i * 80}ms` }}>
            <span className="inline-flex rounded-full p-3" style={{ background: `${m.color}22` }}>
              <m.icon className="w-7 h-7" style={{ color: m.color }} />
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black">{m.name}</h2>
            <p className="text-sm font-bold" style={{ color: m.color }}>{m.tag}</p>
            <p className="mt-3 text-gray-300 leading-relaxed">{m.desc}</p>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-[#0055FF]/40 bg-[#0055FF]/10 p-7 flex items-start gap-4">
          <AlertTriangle className="w-7 h-7 text-[#0055FF] shrink-0" />
          <div>
            <h3 className="font-heading text-xl font-bold">ABS — sur commande spéciale</h3>
            <p className="mt-2 text-gray-300">L'ABS est disponible <strong>uniquement sur commande spéciale et après étude</strong> de votre projet. Ce matériau technique demande des conditions d'impression particulières : contactez-nous pour vérifier la faisabilité.</p>
          </div>
        </div>
        <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-7 flex items-start gap-4">
          <XCircle className="w-7 h-7 text-gray-400 shrink-0" />
          <div>
            <h3 className="font-heading text-xl font-bold">Pas de résine</h3>
            <p className="mt-2 text-gray-300">Nous ne proposons pas d'impression résine. Toutes nos créations sont réalisées par dépôt de filament (PLA, PETG, TPU et ABS sur étude).</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 text-center">
        <Link to="/devis" className="inline-flex btn-primary rounded-full px-7 py-3 font-bold">Un doute sur le matériau ? Demandez conseil</Link>
      </div>
    </div>
  );
}
